<script>
    import { _ } from "svelte-i18n";

    import { onMount, onDestroy } from "svelte";
    import { createEventDispatcher } from "svelte";
    import { api, apiOutside } from "../../utilities/Utils.svelte";
    import { bind } from "svelte/internal";

    export let src;
    // Text of the play button. READ (Listen & Repeat) plays a sentence, not a
    // question.
    export let label = "Listen to the question";
    // Plays allowed, 0 for unlimited. A limited recording has no native
    // controls (they would allow pausing and replaying at will): only the
    // play button, offered again while plays are left.
    export let maxPlays = 0;
    // Start playing as soon as the recording is ready. If the browser blocks
    // it, the play button is there as before.
    export let autoplay = false;

    const dispatch = createEventDispatcher();

    let audioDiv;

    let state = "NEW";

    let audioDivSource;

    let audioPlayer;

    let playsStarted = 0;
    let autoplayTried = false;
    $: limited = maxPlays > 0;
    $: playsLeft = limited ? Math.max(0, maxPlays - playsStarted) : Infinity;

    function isIOS() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    }

    function endedListener() {
        console.log("Audio playback ended");
        state = "ENDED";
        lastAllowedTime = 0;
        dispatch("audioEnded");
    }

    // Tracks the furthest position reached through normal playback.
    // Used to block the user from seeking/skipping ahead.
    let lastAllowedTime = 0;

    function seekingListener() {
        // Snap the playhead back to the last allowed position to disable seeking.
        if (audioDiv && Math.abs(audioDiv.currentTime - lastAllowedTime) > 0.3) {
            audioDiv.currentTime = lastAllowedTime;
        }
    }

    function timeUpdateListener() {
        if (audioDiv && !audioDiv.seeking) {
            lastAllowedTime = audioDiv.currentTime;
        }
    }

    function errorListener(event) {
        state = "ERROR";
        const errorMsg =
            audioDiv?.error?.message ||
            audioDiv?.error?.code ||
            "Unknown error";
        console.error(
            "Audio playback error:",
            event,
            "Error message:",
            errorMsg,
            "Source:",
            audioDiv.src,
        );
        dispatch("audioError", { error: event, message: errorMsg });
    }

    onMount(() => {
        // This script runs when the component is mounted
        console.log("AudioPlayback component mounted");

        // audioDiv = document.getElementById("audioPlaybackDiv");
        // audioDivSource = document.getElementById("audioPlaybackSource");

        if (audioDiv) {
            // audioDiv.addEventListener("ended", endedListener);
            // audioDiv.addEventListener("error", errorListener);
        } else {
            console.error(
                "Audio element not found. Ensure it exists in the DOM.",
            );
        }
        loadAudio();
    });

    onDestroy(() => {
        console.log("AudioPlayback is being destroyed");

        // Clean up retry and load timeouts to prevent orphaned callbacks
        if (retryTimeoutId !== null) {
            clearTimeout(retryTimeoutId);
            retryTimeoutId = null;
        }
        if (loadTimeoutId !== null) {
            clearTimeout(loadTimeoutId);
            loadTimeoutId = null;
        }

        if (audioDiv) {
            try {
                audioDiv?.pause();
                URL.revokeObjectURL(audioDiv.src);
                audioDiv.src = ""; // Clear the source to release resources
            } catch (error) {
                console.error("Error during audio cleanup:", error);
            }

            audioDiv?.removeEventListener("ended", endedListener);

            audioDiv?.removeEventListener("error", errorListener);
            audioDiv.removeEventListener("canplaythrough", handleReady);
        }
    });

    let audioRetryCount = 0;
    const MAX_AUDIO_RETRIES = 3;
    const AUDIO_RETRY_DELAY = 1500;
    let retryTimeoutId = null;
    let loadTimeoutId = null;

    function resolveAudioSrc(value) {
        if (!value) return value;
        if (/^(https?:|blob:|data:)/i.test(value)) {
            return value;
        }
        return "https://storage.googleapis.com/sensay-assessment/images/" + value.replace(/^\/+/, "");
    }

    function loadAudio() {
        // Guard against component being destroyed during retry
        if (!audioDiv) {
            console.warn("loadAudio called but audioDiv is null/undefined - component likely destroyed");
            return;
        }
        
        if (src) {
            const resolvedSrc = resolveAudioSrc(src);
            console.log("Loading audio from source:", resolvedSrc, "attempt:", audioRetryCount + 1);
            dispatch("audioLoading", { src: resolvedSrc });
            
            // Add timeout to prevent infinite hang
            const AUDIO_LOAD_TIMEOUT = 30000; // 30 seconds
            const controller = new AbortController();
            loadTimeoutId = setTimeout(() => controller.abort(), AUDIO_LOAD_TIMEOUT);
            
            apiOutside
                .get(resolvedSrc, { signal: controller.signal })
                .then((response) => response.blob())
                .then((blob) => {
                    clearTimeout(loadTimeoutId);
                    loadTimeoutId = null;
                    
                    // Guard against component being destroyed during fetch
                    if (!audioDiv) {
                        console.warn("Audio loaded but audioDiv is null - component destroyed");
                        return;
                    }
                    
                    state = "LOADING";
                    const audioBlob = new Blob([blob], { type: "audio/mpeg" });
                    console.log("Audio loaded successfully", audioBlob);
                    // const audioUrl = URL.createObjectURL(blob);
                    const audioUrl = URL.createObjectURL(audioBlob);
                    audioDiv.src = audioUrl;
                    audioDiv.load();
                    audioRetryCount = 0; // Reset on success
                })
                .catch((error) => {
                    clearTimeout(loadTimeoutId);
                    loadTimeoutId = null;
                    console.error("Error loading audio:", error, "attempt:", audioRetryCount + 1);
                    
                    // Guard against component being destroyed during fetch
                    if (!audioDiv) {
                        console.warn("Audio fetch failed but audioDiv is null - component destroyed, skipping retry");
                        return;
                    }
                    
                    // Retry logic
                    if (audioRetryCount < MAX_AUDIO_RETRIES) {
                        audioRetryCount++;
                        console.log("Retrying audio load in", AUDIO_RETRY_DELAY, "ms");
                        retryTimeoutId = setTimeout(() => loadAudio(), AUDIO_RETRY_DELAY);
                    } else {
                        state = "ERROR";
                        console.error("Audio load failed after", MAX_AUDIO_RETRIES, "retries");
                        dispatch("audioError", { error: error, message: "Failed to load audio after multiple retries" });
                    }
                });
        } else {
            console.warn("No audio source provided.");
        }
    }

    function playListener() {
        console.log("Audio playback started");
        // A play from the start counts; resuming after a pause does not.
        if (audioDiv && audioDiv.currentTime < 0.5) {
            playsStarted += 1;
        }
        state = "PLAYING";
        dispatch("audioStarted", { src: src });
    }

    function handleReady() {
        console.log("Audio is ready to play");

        if (state === "LOADING" || state === "NEW") {
            state = "READY";
            dispatch("audioReady");
            if (autoplay && !autoplayTried) {
                autoplayTried = true;
                resumeAudio();
            }
        } else {
            console.warn(
                "Audio is not in LOADING state, current state:",
                state,
                "src:",
                src,
                "audioDiv.src:",
                audioDiv.src,
            );
        }
    }

    let starting = false;

    function isIgnorableMediaError(err) {
        return err && (err.name === 'AbortError' || err.name === 'NotAllowedError');
    }

    async function resumeAudio() {
        if (!audioDiv || starting || playsLeft <= 0) return;
        starting = true;
        
        try {
            if (audioDiv.paused) {
                if (state === "ENDED") {
                    audioDiv.currentTime = 0;
                }
                await audioDiv.play();
                state = "PLAYING";
                console.log("Audio playback started");
                dispatch("audioStarted", { src: src });
            }
        } catch (error) {
            if (!isIgnorableMediaError(error)) {
                console.error("Error resuming audio:", error);
                dispatch("audioError", { error: error });
                if (window.TrackJS) window.TrackJS.track(error);
            }
        } finally {
            starting = false;
        }

        if (isIOS() && audioPlayer) {
            audioPlayer.play().catch((error) => {
                if (!isIgnorableMediaError(error)) {
                    console.error("Error playing audio on iOS:", error);
                    state = "ERROR";
                    dispatch("audioError", { error: error });
                }
            });
        }
    }
</script>

<audio
    id="audioPlaybackDiv"
    bind:this={audioDiv}
    preload="auto"
    controls={!limited}
    controlsList="nodownload noplaybackrate"
    class="plain-audio"
    class:disabled={state === "LOADING"}
    on:canplay={handleReady}
    on:canplaythrough={handleReady}
    on:loadedmetadata={handleReady}
    on:ended={endedListener}
    on:error={errorListener}
    on:play={playListener}
    on:seeking={seekingListener}
    on:timeupdate={timeUpdateListener}
>
    <!-- A limited recording plays only from the fetched blob: a native source
         could start before the blob replaces it, and the swap would cut off a
         play that was already counted. -->
    {#if !limited}
        <source bind:this={audioDivSource} {src} type="audio/mp3" />
    {/if}
</audio>
<!-- 
{#if isIOS() && src}
    <audio
        bind:this={audioPlayer}
        preload="auto"
        controls
        controlsList="nodownload"
    >
        {#if src.startsWith("https")}
            <source {src} type="audio/mp3" />
        {:else}
            <source
                src={"https://storage.googleapis.com/sensay-assessment/images/" +
                    src}
                type="audio/mp3"
            />
        {/if}
        <track default kind="captions" srclang="en" src="" />{$_(
            "NOT_SUPPORTED",
        )}</audio
    >
{/if} -->

{#if state === "ERROR" || state === "READY" || (limited && state === "ENDED" && playsLeft > 0)}
    <div class="row justify-content-center mb-2">
        <button class="btn btn-danger" on:click={resumeAudio}
            ><span class="play-icon">▶</span> {label}</button
        >
    </div>
{/if}
{#if limited && state !== "ERROR"}
    <p class="plays-left">{$_("AUDIO_PLAYS_LEFT", { values: { count: playsLeft } })}</p>
{/if}

<style>
    .plain-audio {
        background: transparent;
        color: inherit;
        border: none;
        box-shadow: none;
        outline: none;
        width: 100%;
        max-width: 420px;
    }
    :global(audio.plain-audio::-webkit-media-controls-panel) {
        background-color: transparent !important;
        box-shadow: none !important;
    }
    :global(audio.plain-audio::-webkit-media-controls-enclosure) {
        background-color: transparent !important;
        border-radius: 0 !important;
        box-shadow: none !important;
    }
    .plays-left {
        text-align: center;
        font-size: 0.85rem;
        opacity: 0.75;
        margin: 0;
    }
    .disabled {
        pointer-events: none;
        opacity: 0.5;
    }
    .play-icon {
        font-size: 0.7em;
        vertical-align: baseline;
        line-height: 1;
        display: inline-block;
        position: relative;
        top: -0.05em;
    }
</style>
